import React, { useMemo, useEffect, useCallback } from 'react';
import CompetitionStepView from './CompetitionStepView';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import useCompetitionData from '../../hooks/useCompetitionData';
import { CompetitionStepContainerProps } from '../../types/competition';
import { Routine } from '../../types/registration';

/**
 * Container component that manages state and data fetching for the Competition Step
 * Updated to work with local state first approach - changes are only sent to backend when user clicks continue
 */
const CompetitionStepContainer: React.FC<CompetitionStepContainerProps> = ({
  event,
  pricingSettings,
  criteriaSettings,
  settingsLoading,
  settingsError,
  registrationData,
  updateRegistrationData,
  onNext,
  onBack,
  isDarkMode,
  renderNavigation
}) => {
  // Memoize competition settings to prevent unnecessary re-renders
  const routineStyles = useMemo(() => {
    const styles = criteriaSettings?.styleCategories || 
      criteriaSettings?.styles || 
      criteriaSettings?.data?.styleCategories || 
      criteriaSettings?.criteria?.styleCategories || [];
      
    // Log the extracted style categories for debugging
    console.log('Extracted style categories:', styles);
    
    return styles;
  }, [criteriaSettings]);
  
  // Memoize competition categories
  const competitionCategories = useMemo(() => {
    const categories = criteriaSettings?.competitionCategories || 
      criteriaSettings?.categories ||
      criteriaSettings?.data?.competitionCategories || 
      criteriaSettings?.criteria?.competitionCategories || [];
      
    // Log the extracted competition categories for debugging
    console.log('Extracted competition categories:', categories);
    
    return categories;
  }, [criteriaSettings]);
                       
  const competitionPricingRules = useMemo(() => 
    pricingSettings?.competitionPricing || 
    pricingSettings?.pricing?.competitionPricing || 
    pricingSettings?.data?.competitionPricing || [],
    [pricingSettings]
  );
                                 
  const ageDivisions = useMemo(() => 
    criteriaSettings?.ageGroups || 
    criteriaSettings?.ages || 
    criteriaSettings?.data?.ageGroups || 
    criteriaSettings?.criteria?.ageGroups || [],
    [criteriaSettings]
  );

  // Log age divisions for debugging, specifically the isCompetitive flag
  useEffect(() => {
    if (ageDivisions && ageDivisions.length > 0) {
      console.log('Age divisions loaded for competition:', 
        ageDivisions.map((div: any) => ({
          label: div.label,
          min: div.minAge,
          max: div.maxAge,
          isCompetitive: div.isCompetitive
        }))
      );
    }
  }, [ageDivisions]);

  // Use custom hook for audio playback
  const { currentlyPlaying, toggleAudio } = useAudioPlayer();
  
  // Use the custom hook for competition data management with local state first approach
  const {
    loading,
    error,
    routines,
    selectedRoutines,
    searchQuery,
    setSearchQuery,
    toggleRoutineSelection,
    updateSelectedRoutineOptions,
    getTotalPrice,
    getPriceForRoutine,
    prepareNextStepData,
    editingRoutine,
    isEditModalOpen,
    handleEditRoutine,
    handleUpdateRoutine,
    handleRoutineUpdateComplete,
    setIsEditModalOpen,
    setEditingRoutine,
    showTypeChangeModal,
    pendingRoutineChange,
    typeChangeInfo,
    setShowTypeChangeModal,
    setPendingRoutineChange,
    setTypeChangeInfo,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    routineBeingAdded
  } = useCompetitionData(
    {
      ...registrationData,
      // Pass the dedicated functions if available (though they won't be used in local state first approach)
      addRoutine: registrationData.addRoutine,
      updateRoutine: registrationData.updateRoutine,
      removeRoutine: registrationData.removeRoutine,
      // Pass the loadRoutines function if available
      loadRegistrationRoutines: registrationData.loadRegistrationRoutines,
      // Pass registrationId for reloading routines if needed
      registrationId: registrationData.registrationId
    }, 
    updateRegistrationData, 
    competitionPricingRules, 
    ageDivisions
  );

  // Handle next button click - LOCAL STATE FIRST APPROACH
  const handleNext = useCallback(async () => {
    // Validation: Check if all selected routines have a STYLE selected
    const allRoutinesHaveStyle = selectedRoutines.every((routine: Routine) => 
      routine.style || (registrationData.competition?.routineOptions?.[routine.routineId || '']?.style)
    );
    
    // Also check if all routines have a CATEGORY selected
    const allRoutinesHaveCategory = selectedRoutines.every((routine: Routine) => 
      routine.category || (registrationData.competition?.routineOptions?.[routine.routineId || '']?.category)
    );

    if (!allRoutinesHaveStyle || !allRoutinesHaveCategory) {
      if (!allRoutinesHaveStyle && !allRoutinesHaveCategory) {
        alert('Please select a style and category for all selected routines before proceeding.');
      } else if (!allRoutinesHaveStyle) {
        alert('Please select a style for all selected routines before proceeding.');
      } else {
        alert('Please select a category for all selected routines before proceeding.');
      }
      return;
    }

    try {
      // Prepare data for the next step and send to backend
      console.log('Processing competition data and sending to backend...');
      await prepareNextStepData();
      
      console.log('Competition data processed successfully, navigating to next step');
      // Call the parent's onNext to navigate
      onNext();
    } catch (error) {
      console.error('Error processing competition data:', error);
      alert('Failed to save competition data. Please try again.');
    }
  }, [selectedRoutines, registrationData, prepareNextStepData, onNext]);

  // Handle settings loading and error states
  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (settingsError) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-100 text-red-800'
      }`}>
        <p>Error loading competition settings: {settingsError}</p>
      </div>
    );
  }

  return (
    <CompetitionStepView
      event={event}
      loading={loading}
      error={error}
      routines={routines}
      selectedRoutines={selectedRoutines}
      routineStyles={routineStyles}
      competitionCategories={competitionCategories}
      competitionPricingRules={competitionPricingRules}
      ageDivisions={ageDivisions}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      toggleRoutineSelection={toggleRoutineSelection}
      updateRoutineOptions={updateSelectedRoutineOptions}
      toggleAudio={toggleAudio}
      currentlyPlaying={currentlyPlaying}
      handleEditRoutine={handleEditRoutine}
      calculateRoutinePrice={getPriceForRoutine}
      calculateTotalPrice={getTotalPrice}
      onNext={handleNext}
      onBack={onBack}
      isDarkMode={isDarkMode}
      
      // Edit modal props
      editingRoutine={editingRoutine}
      isEditModalOpen={isEditModalOpen}
      onCloseEditModal={() => setIsEditModalOpen(false)}
      onUpdateRoutine={handleUpdateRoutine}
      onRoutineUpdateComplete={handleRoutineUpdateComplete}
      
      // Type change modal props
      showTypeChangeModal={showTypeChangeModal}
      typeChangeInfo={typeChangeInfo}
      onConfirmTypeChange={handleConfirmTypeChange}
      onCancelTypeChange={handleCancelTypeChange}
      
      // Pass the mappings from registration data
      routineMappings={registrationData.competition?.mappings || {}}
      
      // Pass the loading state
      routineBeingAdded={routineBeingAdded}
      renderNavigation={renderNavigation}
    />
  );
};

export default CompetitionStepContainer; 